import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Briefcase, Upload, MessageSquare, Mic, FileArchive, Loader2 } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import axiosInstance from "../../services/api/axios";
import { getPresignedDownloadUrl, getPresignedUploadUrls, uploadFilesToS3 } from "../../utils/fileUtils";
import ChatPanel from "../../components/marketplace/ChatPanel";
import DeliverableThread from "../../components/marketplace/DeliverableThread";
import { listMyOffers, listDeliverables, submitDeliverable } from "../../services/archMarketplace";

async function openKey(key) {
  try {
    const res = await getPresignedDownloadUrl(key, axiosInstance);
    const url = res?.download_url || res?.downloadUrl;
    if (url) window.open(url, "_blank");
  } catch {
    toast.error("Could not open file.");
  }
}

function FreelancerAssignedProject() {
  const { projectId } = useParams();
  const [offer, setOffer] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    const [offers, d] = await Promise.all([
      listMyOffers(false),
      listDeliverables(projectId).catch(() => []),
    ]);
    const mine = (offers || []).find((o) => String(o.archProjectId) === String(projectId));
    setOffer(mine || null);
    setDeliverables(Array.isArray(d) ? d : []);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await refresh();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const latest = deliverables[deliverables.length - 1];
  const awaitingReview = latest?.status === "SUBMITTED";
  const completed = latest?.status === "ACCEPTED";
  const canUpload = !awaitingReview && !completed;

  const handleUpload = async () => {
    if (!files.length) {
      toast.error("Choose at least one file.");
      return;
    }
    setUploading(true);
    try {
      const presigned = await getPresignedUploadUrls(files, "deliverables", axiosInstance);
      const uploadData = presigned.map((p, i) => ({
        file: files[i],
        upload_url: p.upload_url,
        content_type: p.content_type || files[i].type,
      }));
      await uploadFilesToS3(uploadData);
      const s3Keys = presigned.map((p) => p.s3_key);
      await submitDeliverable(projectId, s3Keys, note.trim() || null);
      toast.success("Design submitted for review.");
      setFiles([]);
      setNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;
  if (!offer) return <div className="p-6 text-gray-400">Project not found.</div>;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={offer.projectName}
        subtitle="Assigned project"
        icon={Briefcase}
        backTo="/freelancer/dashboard"
        backLabel="Back to my work"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Brief */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Brief</h3>
              {offer.freelancerAmount != null && (
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700">
                  You earn ₹{Number(offer.freelancerAmount).toLocaleString("en-IN")}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.description}</p>
            {offer.voiceTranscript && (
              <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                <span className="font-medium">Voice note transcript: </span>
                {offer.voiceTranscript}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {offer.voiceNoteKey && (
                <button
                  onClick={() => openKey(offer.voiceNoteKey)}
                  className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
                >
                  <Mic size={14} /> Voice note
                </button>
              )}
              {offer.zipKey && (
                <button
                  onClick={() => openKey(offer.zipKey)}
                  className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
                >
                  <FileArchive size={14} /> Project files (ZIP)
                </button>
              )}
            </div>
          </div>

          {/* Deliverables + upload */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Your Deliverables &amp; Revisions
            </h3>

            <div className="mb-4">
              <DeliverableThread deliverables={deliverables} perspective="FREELANCER" onOpen={openKey} />
            </div>

            {completed ? (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                This project is complete. BuildVizer will process your reimbursement.
              </p>
            ) : awaitingReview ? (
              <p className="rounded-md bg-purple-50 px-3 py-2 text-sm text-purple-700">
                Your latest submission is awaiting the client's review.
              </p>
            ) : (
              <div className="border-t pt-4 space-y-3">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm"
                />
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note for the client…"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-300"
                />
                <button
                  onClick={handleUpload}
                  disabled={uploading || !canUpload}
                  className="inline-flex items-center gap-2 rounded-md bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? "Uploading…" : "Submit Design"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Chat */}
        <div className="space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-600">
            <MessageSquare size={16} /> Q&amp;A
          </h3>
          <ChatPanel projectId={projectId} myEntity="FREELANCER" />
        </div>
      </div>
    </div>
  );
}

export default FreelancerAssignedProject;
